import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Check, ShieldCheck, Zap, ChevronDown } from "lucide-react";
import { COUNTRIES, AFRICA_ISO } from "../lib/countries";
import { useCountryAutoDetect } from "../lib/useCountryAutoDetect";

/**
 * Landing-page Pricing section — country-aware.
 *
 * Shows exactly TWO plans (Regular / Emergency) with the price for the
 * visitor's selected country. Numbers match `pricing_service.py` which
 * is the backend source of truth used at checkout:
 *
 *   India                → ₹1,000  / ₹2,000    (Cashfree)
 *   Africa (54 ISO-2)    → $50 USD / $100 USD  (PayPal)
 *   Rest of world        → $100 USD / $150 USD (PayPal)
 *
 * Country is auto-detected on mount (IP → browser locale → India). The
 * visitor can manually change it — no options exist below this section
 * for arbitrage (booking wizard locks it after signup).
 */
const PLAN_META = [
  {
    key: "regular",
    name: "Regular Second Opinion",
    icon: ShieldCheck,
    sla: "Within 48 hours",
    pitch: "Detailed written second opinion from a verified specialist.",
    perks: [
      "NIMHANS / AIIMS alumni network",
      "Detailed written report",
      "Prescription (if needed)",
      "Secure chat with the doctor",
      "Reports available in your language",
    ],
    highlight: true,
    tag: "Most popular",
  },
  {
    key: "emergency",
    name: "Priority or Video Consultation",
    icon: Zap,
    sla: "Within 12-24 hours",
    pitch: "Priority queue routing OR a scheduled video consultation — your case jumps ahead and is reviewed on the same day.",
    perks: [
      "Everything in Regular",
      "Priority queue (<24 hrs)",
      "Choose Written or 20-min HD Video at booking",
      "Same specialist for follow-up (paid) if needed later",
      "Money-back if no response in 24 hrs",
    ],
    highlight: false,
    tag: "Priority",
  },
];

function priceFor(iso, planKey) {
  if (iso === "IN") return { amount: planKey === "regular" ? 1000 : 2000, currency: "INR", symbol: "₹", suffix: "" };
  if (AFRICA_ISO.has(iso)) return { amount: planKey === "regular" ?   50 :  100, currency: "USD", symbol: "$", suffix: " USD" };
  return                   { amount: planKey === "regular" ?  100 :  150, currency: "USD", symbol: "$", suffix: " USD" };
}

export default function PricingSection() {
  const { country, setCountry } = useCountryAutoDetect("IN");
  // Emergency-card format selector — stored in sessionStorage so it flows
  // through signup → PatientDashboard case-create prefill.
  const [emergencyFormat, setEmergencyFormat] = useState(() => {
    try { return sessionStorage.getItem("noi_emergency_format") || "written"; }
    catch (e) { return "written"; }
  });
  const setFormat = (f) => {
    setEmergencyFormat(f);
    try { sessionStorage.setItem("noi_emergency_format", f); } catch (e) { /* private mode */ }
  };

  return (
    <section className="bg-gradient-to-b from-white to-slate-50 border-b border-slate-200" id="pricing">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-2xl">
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0B5ED7]">Pricing</div>
          <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold text-slate-900 leading-tight">
            Transparent fees for your country. <span className="gradient-text">No hidden costs.</span>
          </h2>
          <p className="mt-3 text-slate-600 text-sm sm:text-base leading-relaxed">
            Flat fee per consultation — no subscriptions, no surprise charges.
            100% refund if a specialist is unable to accept your case.
          </p>
        </div>

        {/* Country picker → dynamically re-prices the two cards */}
        <div className="mt-8 inline-flex items-center gap-3 bg-white border border-slate-200 rounded-full px-4 py-2 shadow-sm">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Your country</span>
          <div className="relative">
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              data-testid="pricing-country-select"
              className="h-8 pr-7 pl-2 rounded-md border border-slate-200 bg-slate-50 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0B5ED7]/30 appearance-none"
            >
              {COUNTRIES.map((c) => <option key={c.iso} value={c.iso}>{c.name}</option>)}
            </select>
            <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"/>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl">
          {PLAN_META.map((p) => {
            const Icon = p.icon;
            const price = priceFor(country, p.key);
            return (
              <Card
                key={p.key}
                className={
                  "relative p-6 border-slate-200 flex flex-col " +
                  (p.highlight
                    ? "border-[#0B5ED7] ring-2 ring-[#0B5ED7]/20 shadow-xl md:-translate-y-2 bg-white"
                    : "bg-white hover:shadow-md transition")
                }
                data-testid={`pricing-${p.key}`}
              >
                {p.tag && (
                  <span className={
                    "absolute -top-3 left-6 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full " +
                    (p.highlight ? "bg-[#0B5ED7] text-white" : "bg-amber-100 text-amber-800 border border-amber-200")
                  }>
                    {p.tag}
                  </span>
                )}
                <div className={"w-10 h-10 rounded-lg grid place-items-center " + (p.highlight ? "bg-[#0B5ED7] text-white" : "bg-slate-100 text-[#0B5ED7]")}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="mt-4 font-heading font-semibold text-xl text-slate-900">{p.name}</div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="font-heading font-bold text-4xl text-slate-900" data-testid={`pricing-amount-${p.key}`}>
                    {price.symbol}{price.amount.toLocaleString()}{price.suffix}
                  </span>
                  <span className="text-xs text-slate-500">per case</span>
                </div>
                <p className="mt-1 text-[11px] font-semibold text-emerald-700">{p.sla}</p>
                <p className="mt-3 text-sm text-slate-600">{p.pitch}</p>
                <ul className="mt-5 space-y-2 flex-1">
                  {p.perks.map((x, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className={"w-4 h-4 mt-0.5 shrink-0 " + (p.highlight ? "text-[#20C997]" : "text-emerald-600")} />
                      {x}
                    </li>
                  ))}
                </ul>
                {p.key === "emergency" && (
                  <>
                    {/* Format picker — patient tells us upfront whether they
                        want a fast written report or a live video slot. Same
                        price, different fulfilment. Choice persists via
                        sessionStorage so signup/booking wizards can prefill. */}
                    <div className="mt-4" data-testid="emergency-format-picker">
                      <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
                        How would you like to consult?
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setFormat("written")}
                          data-testid="emergency-format-written"
                          className={
                            "text-xs font-semibold rounded-lg border px-3 py-2 flex items-center justify-center gap-1.5 transition " +
                            (emergencyFormat === "written"
                              ? "bg-slate-900 border-slate-900 text-white shadow-sm"
                              : "bg-white border-slate-200 text-slate-700 hover:border-slate-300")
                          }
                        >
                          📝 Written · &lt;24h
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormat("video")}
                          data-testid="emergency-format-video"
                          className={
                            "text-xs font-semibold rounded-lg border px-3 py-2 flex items-center justify-center gap-1.5 transition " +
                            (emergencyFormat === "video"
                              ? "bg-violet-700 border-violet-700 text-white shadow-sm"
                              : "bg-white border-slate-200 text-slate-700 hover:border-slate-300")
                          }
                        >
                          🎥 Video · 20-min HD
                        </button>
                      </div>
                    </div>
                    <div className="mt-4 text-[10.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2 leading-snug">
                      <b>Note:</b> Priority second-opinion service — <b>not a substitute for emergency medical care</b>. For life-threatening symptoms, please visit your nearest hospital immediately.
                    </div>
                  </>
                )}
                <Link
                  to={p.key === "emergency" ? `/login?as=patient&fmt=${emergencyFormat}` : "/login?as=patient"}
                  className="mt-6"
                >
                  <Button
                    className={
                      "w-full h-11 " +
                      (p.key === "emergency" && emergencyFormat === "video"
                        ? "bg-violet-700 hover:bg-violet-800 text-white shadow-md"
                        : p.highlight
                          ? "bg-[#0B5ED7] hover:bg-[#084BB0] text-white shadow-md"
                          : "bg-slate-900 hover:bg-slate-800 text-white")
                    }
                    data-testid={`pricing-cta-${p.key}`}
                  >
                    {p.key === "emergency"
                      ? (emergencyFormat === "video" ? "🎥 Book Video Consultation" : "📝 Book Priority Opinion")
                      : `Book ${p.name.split(" ")[0]}`}
                  </Button>
                </Link>
              </Card>
            );
          })}
        </div>

        <div className="mt-10 inline-flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 px-3 py-1.5 rounded-full border border-slate-200 bg-white">
            <ShieldCheck className="w-3.5 h-3.5 text-[#20C997]"/> 100% money-back guarantee
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 px-3 py-1.5 rounded-full border border-slate-200 bg-white">
            🔒 Encrypted scan storage
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 px-3 py-1.5 rounded-full border border-slate-200 bg-white">
            🇮🇳 India via Cashfree · 🌍 Others via PayPal
          </span>
        </div>
      </div>
    </section>
  );
}
